/**
 * Composable для хранения прогресса пользователя в системе справки.
 * Сохраняет данные в localStorage, чтобы не показывать туры повторно.
 */
import { reactive, watch } from 'vue'

const STORAGE_KEY = 'cloudgps_help_progress'

// Дефолтное состояние прогресса
const defaultProgress = {
  // Завершённые туры (массив идентификаторов туров)
  completedTours: [],
  // Скрытые подсказки (массив ключей подсказок, которые пользователь нажал "Понятно")
  dismissedTooltips: [],
  // Дата первого входа пользователя
  firstLoginDate: null,
  // Завершён ли onboarding тур
  onboardingCompleted: false,
  // Версия справки (для показа новых фич после обновления)
  lastHelpVersion: '1.0.0',
}

// Загружаем сохранённый прогресс из localStorage
const loadProgress = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      return { ...defaultProgress, ...parsed }
    }
  } catch (e) {
    console.warn('Не удалось загрузить прогресс справки:', e)
  }
  return { ...defaultProgress }
}

// Сохраняем прогресс в localStorage
const saveProgress = (progress) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
  } catch (e) {
    console.warn('Не удалось сохранить прогресс справки:', e)
  }
}

// Реактивное состояние прогресса (синглтон)
const progress = reactive(loadProgress())

// Автосохранение при изменениях
watch(
  () => ({ ...progress }),
  (newVal) => saveProgress(newVal),
  { deep: true }
)

export function useHelpProgress() {
  /**
   * Проверяет, завершён ли указанный тур.
   * @param {string} tourId - идентификатор тура
   * @returns {boolean}
   */
  const isTourCompleted = (tourId) => {
    return progress.completedTours.includes(tourId)
  }

  /**
   * Отмечает тур как завершённый.
   * @param {string} tourId - идентификатор тура
   */
  const completeTour = (tourId) => {
    if (!progress.completedTours.includes(tourId)) {
      progress.completedTours.push(tourId)
    }
  }

  /**
   * Проверяет, скрыта ли подсказка.
   * @param {string} tooltipKey - ключ подсказки
   * @returns {boolean}
   */
  const isTooltipDismissed = (tooltipKey) => {
    return progress.dismissedTooltips.includes(tooltipKey)
  }

  /**
   * Скрывает подсказку (пользователь нажал "Понятно").
   * @param {string} tooltipKey - ключ подсказки
   */
  const dismissTooltip = (tooltipKey) => {
    if (!progress.dismissedTooltips.includes(tooltipKey)) {
      progress.dismissedTooltips.push(tooltipKey)
    }
  }

  /**
   * Проверяет, нужно ли показать onboarding.
   * @returns {boolean}
   */
  const shouldShowOnboarding = () => {
    return !progress.onboardingCompleted
  }

  /**
   * Отмечает onboarding как завершённый.
   */
  const completeOnboarding = () => {
    progress.onboardingCompleted = true
    if (!progress.firstLoginDate) {
      progress.firstLoginDate = new Date().toISOString()
    }
  }

  /**
   * Сбрасывает весь прогресс (для повторного прохождения туров).
   */
  const resetProgress = () => {
    progress.completedTours = []
    progress.dismissedTooltips = []
    progress.onboardingCompleted = false
  }

  /**
   * Сбрасывает только подсказки (показать все снова).
   */
  const resetTooltips = () => {
    progress.dismissedTooltips = []
  }

  /**
   * Сбрасывает конкретный тур для повторного прохождения.
   * @param {string} tourId - идентификатор тура
   */
  const resetTour = (tourId) => {
    const index = progress.completedTours.indexOf(tourId)
    if (index > -1) {
      progress.completedTours.splice(index, 1)
    }
  }

  return {
    progress,
    isTourCompleted,
    completeTour,
    isTooltipDismissed,
    dismissTooltip,
    shouldShowOnboarding,
    completeOnboarding,
    resetProgress,
    resetTooltips,
    resetTour,
  }
}
